import {
  BzDateFns,
  type Guid,
  getDueDate,
  guidSchema,
  type InvoiceTermV2,
  type IsoDateString,
  type LocalDateString,
  type TimeZoneId,
} from '@breezy/shared'
import type { IssueInvoiceModalResult } from './components/IssueInvoiceModal'

export const resolveServiceCompletionDateForDisplay = (
  serviceCompletionDate: LocalDateString | null | undefined,
): LocalDateString | undefined => serviceCompletionDate ?? undefined

export type InvoiceCreationAppointment = {
  jobAppointmentGuid: Guid
  appointmentWindowStart?: IsoDateString | null
}

type ResolveInvoiceCreationServiceCompletionDateInput = {
  appointmentQueryParam?: string | null
  jobAppointments: ReadonlyArray<InvoiceCreationAppointment>
}

type ResolveInvoiceCreationServiceCompletionDateResult = {
  serviceCompletionDate?: LocalDateString
  validatedJobAppointmentGuid?: Guid
}

export const resolveInvoiceCreationServiceCompletionDate = ({
  appointmentQueryParam,
  jobAppointments,
}: ResolveInvoiceCreationServiceCompletionDateInput): ResolveInvoiceCreationServiceCompletionDateResult => {
  if (!appointmentQueryParam) {
    return {}
  }

  const parsedAppointmentGuid = guidSchema.safeParse(appointmentQueryParam)
  if (!parsedAppointmentGuid.success) {
    return {}
  }

  const matchingAppointment = jobAppointments.find(
    appointment => appointment.jobAppointmentGuid === parsedAppointmentGuid.data,
  )

  if (!matchingAppointment) {
    return {}
  }

  return {
    validatedJobAppointmentGuid: matchingAppointment.jobAppointmentGuid,
  }
}

type ResolveInvoiceDueAtOnSaveInput = {
  saveDraft?: boolean
  existingInvoice?: ExistingInvoiceDateState
  nextInvoiceTerm: InvoiceTermV2
  tzId: TimeZoneId
  issuedAtOverride?: IsoDateString
}

type ResolveInvoiceDueAtOnSaveResult = {
  issuedAt?: IsoDateString
  dueAt?: IsoDateString
}

export type ExistingInvoiceDateState = {
  issuedAt?: IsoDateString | null
  dueAt?: IsoDateString | null
  invoiceTerm: InvoiceTermV2
}

export const resolveInvoiceDueAtOnSave = ({
  saveDraft,
  existingInvoice,
  nextInvoiceTerm,
  tzId,
  issuedAtOverride,
}: ResolveInvoiceDueAtOnSaveInput): ResolveInvoiceDueAtOnSaveResult => {
  if (saveDraft) {
    return {
      issuedAt: undefined,
      dueAt: undefined,
    }
  }

  const resolvedIssuedAt = issuedAtOverride ?? existingInvoice?.issuedAt ?? BzDateFns.nowISOString()
  const persistedDueAt = existingInvoice?.dueAt
  const canReusePersistedDueAt =
    issuedAtOverride === undefined && persistedDueAt != null && existingInvoice?.invoiceTerm === nextInvoiceTerm

  return {
    issuedAt: resolvedIssuedAt,
    dueAt: canReusePersistedDueAt ? persistedDueAt : getDueDate(resolvedIssuedAt, nextInvoiceTerm, tzId),
  }
}

type ResolveStreamlinedIssueInvoiceResultInput = {
  serviceCompletionDate: LocalDateString | undefined
  invoiceTerm: InvoiceTermV2
  tzId: TimeZoneId
}

export const resolveStreamlinedIssueInvoiceResult = ({
  serviceCompletionDate,
  invoiceTerm,
  tzId,
}: ResolveStreamlinedIssueInvoiceResultInput): IssueInvoiceModalResult => {
  const resolvedServiceCompletionDate = serviceCompletionDate ?? BzDateFns.getTodayLocalDate(tzId)
  const issuedAt = BzDateFns.nowISOString()
  const dueAt = getDueDate(issuedAt, invoiceTerm, tzId)
  return {
    serviceCompletionDate: resolvedServiceCompletionDate,
    invoiceTerm,
    issuedAt,
    dueAt,
  }
}
